import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Calendar, MessageCircle, Heart, RefreshCw, BookOpen } from 'lucide-react';
import { usePortfolioData } from '../utils/usePortfolioData';

const Writeups: React.FC = () => {
  const { profile, loading, error, refreshData } = usePortfolioData();

  if (loading) {
    return (
      <section id="writeups" className="section-padding bg-cyber-dark">
        <div className="container-custom">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="inline-block mb-4"
              >
                <RefreshCw size={32} className="text-cyber-blue" />
              </motion.div>
              <p className="text-gray-400">Loading portfolio data...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="writeups" className="section-padding bg-cyber-dark">
        <div className="container-custom">
          <div className="text-center">
            <p className="text-red-400 mb-4">Error loading portfolio data: {error}</p>
            <button
              onClick={refreshData}
              className="bg-cyber-blue text-white px-4 py-2 rounded-lg hover:bg-cyber-purple transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (!profile || !profile.posts || profile.posts.length === 0) {
    return (
      <section id="writeups" className="section-padding bg-cyber-dark">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold gradient-text mb-4">
              Writeups
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              No writeups available at the moment
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="writeups" className="section-padding bg-cyber-dark">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <h2 className="text-3xl sm:text-4xl font-bold gradient-text mb-6">
            Writeups
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Detailed walkthroughs and solutions for CTF challenges and security research
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-10">
          {profile.posts.map((post, index) => (
            <motion.article
              key={post.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group bg-cyber-darker rounded-lg p-8 border border-cyber-blue/20 hover:border-cyber-blue/40 transition-all duration-300"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-cyber-blue to-cyber-purple rounded-lg">
                    <BookOpen size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{post.title}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <Calendar size={14} className="text-cyber-purple" />
                      <span className="text-cyber-purple font-mono text-sm">{post.timestamp}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <p className="text-gray-300 leading-relaxed text-lg mb-6">
                {post.content}
              </p>

              {/* Engagement Metrics */}
              <div className="flex items-center gap-6 mb-6 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <Heart size={16} className="text-red-400" />
                  <span>{post.likes}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MessageCircle size={16} className="text-cyber-blue" />
                  <span>{post.comments}</span>
                </div>
                <div className="flex items-center gap-2">
                  <ExternalLink size={16} className="text-cyber-purple" />
                  <span>{post.shares}</span>
                </div>
              </div>

              {/* Hashtags */}
              <div className="flex flex-wrap gap-2 mb-6">
                {post.hashtags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-cyber-dark/50 border border-cyber-blue/20 rounded-full text-xs text-cyber-blue font-mono"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              {/* Read More Link */}
              <div className="pt-4 border-t border-gray-800">
                <a
                  href={post.externalLink || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-cyber-blue hover:text-cyber-purple transition-colors"
                >
                  <span className="text-sm">Read Writeup</span>
                  <ExternalLink size={16} />
                </a>
              </div>
            </motion.article>
          ))}
        </div>

        {/* View All Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <a
            href={profile.socialLinks.medium}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-cyber-blue to-cyber-purple text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300"
          >
            <BookOpen size={20} />
            View All Writeups on Medium
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Writeups; 